import React, { Fragment, useState } from 'react'
import {
    StyledVideo,
    videoClassname,
    StyledWrapper,
    StyledStickyCloseButton,
} from '../../content/Video/Video.styled'
import { UnmuteButton } from '../../content/Video/components/UnmuteButton/UnmuteButton'
import { getHumanDuration } from '../../content/Video/utils/get-human-duration'
import { PlayOverlay } from '../../content/Video/components/PlayOverlay/PlayOverlay'
import { TitleOverlay } from '../../content/Video/components/TitleOverlay/TitleOverlay'
import { TopBar } from '../../content/Video/components/TopBar/TopBar'
import { RenderPlayerParams } from './player-render-params'
import { getPosterImage } from './utils/poster'
import { VideoPlaylist } from './components/VideoPlaylist/VideoPlaylist'
import debounce from 'lodash.debounce'
import { KickerPlayButton } from './components/KickerPlayButton/KickerPlayButton'
import { scaleImage } from '@news-mono/web-common'
import { getImageWidth } from './utils/product-info'
import { Product } from '@news-mono/web-common'
import { StickyCloseIcon } from './components/StickyCloseIcon/StickyCloseIcon'

export const videoWrapperClassName = 'video-wrapper'

export const VideoSkin: React.FC<RenderPlayerParams> = ({
    theme,
    wrapperElProps,
    videoElement,
    playlistProps,
    playerState,
    playerOptions,
    playerActions,
}) => {
    const [hideSticky, setHideSticky] = useState(false)

    const topBarVisible =
        playerState.adPlayed === true &&
        (!playerOptions.compactMode || theme.kind === 'sevennews') &&
        playerState.showTopBar

    const renderUnmuteButton = playerState.isMuted &&
        !playerState.showTitleOverlay &&
        !playerState.showInternalPlayButton && (
            <UnmuteButton
                positionState={{
                    alignToTop: playerOptions.compactMode,
                    isLowered:
                        (!playerState.showTopBar && playerState.adPlayed) ||
                        (!playerState.showTopBar && playerState.adPlaying),
                }}
                action={playerActions.handleMuteToggle}
            />
        )

    const renderCloseButton = playerOptions.compactMode &&
        theme.kind === Product.SevenNews && (
            <StyledStickyCloseButton
                onClick={() => {
                    playerActions.closeStickyVideo()
                    setHideSticky(true)
                }}
            >
                <StickyCloseIcon />
            </StyledStickyCloseButton>
        )

    const humanDuration = playerState.currentVideoMetadata
        ? getHumanDuration(playerState.currentVideoMetadata.duration)
        : process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'vr_test'
        ? '1:23'
        : undefined
    const renderTopBar = (
        <TopBar
            theme={theme}
            compactMode={playerOptions.compactMode}
            isVisible={topBarVisible && !playerState.isFullscreen}
            videoDuration={humanDuration}
            videoDetails={playerState.currentVideo}
        />
    )

    const renderPlayOverlay = !playerState.showInternalPlayButton ? (
        false
    ) : playerOptions.playButtonText ? (
        <KickerPlayButton
            type={playerState.currentVideo.videoType}
            text={playerOptions.playButtonText}
            duration={humanDuration}
            play={playerActions.playVideo}
            playable={playerState.currentVideoMetadata !== undefined}
        />
    ) : (
        <PlayOverlay play={playerActions.playVideo} isCentered={true} />
    )

    const renderTitleOverlay = playerState.showTitleOverlay && (
        <TitleOverlay
            play={playerActions.playVideo}
            cancelAutoplay={playerActions.stopAutoplay}
            videoDuration={
                playerState.currentVideoMetadata
                    ? getHumanDuration(
                          playerState.currentVideoMetadata.duration,
                      )
                    : undefined
            }
            compactMode={playerOptions.compactMode || false}
            videoDetails={playerState.currentVideo}
            autoplayRemainingTime={
                playerState.currentState.type === 'autoplay-countdown'
                    ? playerState.currentState.remainingTime
                    : undefined
            }
        />
    )

    const { innerRef, ...StyledVideoWrapperProps } = wrapperElProps
    const posterImageCrop = getPosterImage(playerState.currentVideo)
    let posterImageScaled: string | undefined

    if (posterImageCrop) {
        posterImageScaled = scaleImage(
            posterImageCrop.reference,
            getImageWidth(theme.kind),
            false,
        )
    }

    return (
        <Fragment>
            {posterImageScaled && (
                <style
                    dangerouslySetInnerHTML={{
                        __html: `
                            @media print {
                                .${videoClassname}::before {
                                    content: url("${posterImageScaled}");
                                    max-width: 600px;
                                }
                            }`,
                    }}
                />
            )}
            <StyledWrapper
                className={videoWrapperClassName}
                compactMode={playerOptions.compactMode}
                hide={hideSticky && playerOptions.compactMode}
            >
                {renderPlayOverlay}

                <StyledVideo
                    {...StyledVideoWrapperProps}
                    ref={innerRef}
                    posterImage={posterImageScaled}
                >
                    {renderTopBar}
                    {renderTitleOverlay}

                    {videoElement}
                    {renderUnmuteButton}
                    {renderCloseButton}
                </StyledVideo>
            </StyledWrapper>
            {playlistProps && (
                <VideoPlaylist
                    hide={hideSticky && playerOptions.compactMode}
                    compactMode={playerOptions.compactMode}
                    showUpNextLabel={theme.kind !== Product.PerthNow}
                    activeItemId={playlistProps.currentVideoId}
                    isLoaded={playlistProps.videoQueue.items.length !== 0}
                    onSelect={playlistProps.handlePlaylistSelect}
                    onScroll={debounce(playlistProps.handlePlaylistScroll, 500)}
                    videoQueue={
                        playlistProps.initialVideoMetadata
                            ? [
                                  playlistProps.initialVideoMetadata,
                                  ...playlistProps.videoQueue.items,
                              ]
                            : []
                    }
                />
            )}
        </Fragment>
    )
}
